import { Injectable } from '@angular/core';
import { DpFieldColor, DpFieldGroup, DpFieldNumber, DpFieldRowList, DpFieldSelect, DpFieldText, DpFormRow, DpModalForm, DpModalFormService, DpModalFormSize } from '@docpath/webcomponents';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { Constants } from '../../../../constants/constants';
import { Alias } from '../../alias/models/alias';
import { AlertRule, LowerHigher, MeasuredResource, MessageContent, Priority, RuleAlias, TimeUnit, Via } from '../models/alert-rule';
import { AlertRulesService } from './alert-rules.service';
import { AlertRulesHttpService } from './alert-rules-http.service';

@Injectable({
    providedIn: 'root'
})
export class AlertRulesFormService {

    readonly i18nPrefix = 'alert-rules-config.form';

    constructor(
        private readonly modalFormService: DpModalFormService,
        private readonly alertRulesHttpService: AlertRulesHttpService,
        private readonly alertRulesService: AlertRulesService,
        private readonly translateService: TranslateService
    ) {}

    initModalForm(alias: Alias[]): DpModalForm {

        return new DpModalForm({
            header: `${this.i18nPrefix}.header.new-rule`,
            rows: [
                new DpFormRow([
                    new DpFieldText({ name: Constants.CONF_ALERT_RULES_RULE_NAME, required: true, column: 7 }),
                    new DpFieldColor({ name: Constants.CONF_ALERT_RULES_COLOR, column: 1, labelColumn: 2, required: true})
                ]),
                new DpFormRow([
                    new DpFieldSelect({
                        name: Constants.CONF_ALERT_RULES_PRIORITY,
                        options: [
                            { value: Priority.Critical },
                            { value: Priority.Medium },
                            { value: Priority.Low }
                        ],
                        required: true,
                        column: 7
                    })
                ]),
                new DpFormRow([
                    new DpFieldSelect({
                        name: Constants.CONF_ALERT_RULES_MEASURED_RESOURCE,
                        options: [
                            { value: MeasuredResource.CPU },
                            { value: MeasuredResource.RAM },
                            { value: MeasuredResource.Disk },
                            { value: MeasuredResource.Java }
                        ],
                        column: 4,
                        label: `${this.i18nPrefix}.measured_resource.label`
                    }),
                    new DpFieldSelect({
                        name: Constants.CONF_ALERT_RULES_LOWER_HIGHER,
                        options: [
                            { value: LowerHigher.Lower },
                            { value: LowerHigher.Higher }
                        ],
                        required: true,
                        column: 3,
                        labelHidden: true
                    }),
                    new DpFieldGroup({
                        fields: [
                            new DpFieldNumber({ name: Constants.CONF_ALERT_RULES_PERCENT_LIMIT, required: true, min: 1, max: 99, column: 2 }),
                            new DpFieldText({ name: Constants.CONF_ALERT_RULES_PERCENT_LIMIT_LABEL, placeholder: '%', disabled: true, column: 1 })
                        ], 
                        labelHidden: true,
                        column: 3
                    })
                ]),
                new DpFormRow([
                    new DpFieldSelect({
                        name: Constants.CONF_ALERT_RULES_TIME_TO_ALERT_FOR,
                        options: [
                            { text: 'For', value: 'for' }
                        ],
                        disabled: true,
                        column: 4,
                        label: '',
                        labelColumn: 2
                    }),
                    new DpFieldNumber({ name: Constants.CONF_ALERT_RULES_TIME_TO_ALERT, required: true, min: 1, max: 59, labelHidden: true, column: 3 }),
                    new DpFieldSelect({
                        name: Constants.CONF_ALERT_RULES_TIME_UNIT,
                        options: [
                            { value: TimeUnit.Minutes }
                        ],
                        required: true,
                        column: 3,
                        labelHidden: true
                    })
                ]),
                new DpFormRow([
                    new DpFieldRowList({
                        name: Constants.CONF_ALERT_RULES_RULE_ALIAS,
                        label: `${this.i18nPrefix}.rule-alias.label`,
                        labelColumn: 2,
                        column: 12,
                        fields: [
                            new DpFieldSelect({
                                name: Constants.CONF_ALERT_RULES_RULE_ALIAS_VIA,
                                options: [
                                    { value: Via.Email },
                                    { value: Via.SMS }
                                ],
                                required: true,
                                column: 4,
                                labelHidden: true
                            }),
                            new DpFieldSelect({
                                name: Constants.CONF_ALERT_RULES_RULE_ALIAS_ALIAS,
                                options: alias.map(a => ({value: a.id, text: a.alert_alias})),
                                required: true,
                                column: 4,
                                labelHidden: true
                            })
                        ],
                        validateFirstFieldUnique: true
                    })
                ])
            ],
            size: DpModalFormSize.Lg,
            i18nPrefix: this.i18nPrefix
        });
    
    }

    openCreationAlertRuleForm(alias: Alias[]): void {

        const form = this.initModalForm(alias);

        form.submit = (value, activeModal) => {
            this.postAlertRules(this.getAlertRuleFromForm(0, value, alias), activeModal);
        };

        this.modalFormService.open(form);

    }

    private postAlertRules(alertRule: AlertRule, activeModal: NgbModalRef): void {

        this.alertRulesHttpService.createAlertRule(alertRule, () => {
            activeModal.close();
            this.alertRulesService.notifyAlertRuleChange();
        });

    }

    openEditAlertRuleForm(alertRule: AlertRule, alias: Alias[]): void {

        const form = this.initModalForm(alias);
        form.header = `${this.i18nPrefix}.header.edit-rule`;

        const modalReference = this.modalFormService.open(form);

        form.submit = (value, activeModal) => {
            this.putAlertRule(this.getAlertRuleFromForm(alertRule.id, value, alias), activeModal);
        };

        modalReference.shown.subscribe(() => {
            form.setValue(({
                rule_name: alertRule.rule_name,
                color: alertRule.color,
                priority: alertRule.priority,
                measured_resource: alertRule.measured_resource,
                lower_higher: alertRule.lower_higher,
                percent_limit: alertRule.percent_limit,
                time_to_alert: alertRule.time_to_alert,
                rule_alias: alertRule.rule_alias.map(rule_alias => ({
                    via: rule_alias.via,
                    alias: rule_alias.alias.id
                }))
            }));
        });

    }

    private putAlertRule(alertRule: AlertRule, activeModal: NgbModalRef): void {

        this.alertRulesHttpService.editAlertRule(alertRule, () => {
            activeModal.close();
            this.alertRulesService.notifyAlertRuleChange();
        });

    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getAlertRuleFromForm(id: number, value: any, alias: Alias[]): AlertRule {

        return new AlertRule(
            id,
            value.color,
            `${value.measured_resource} ${value.lower_higher} ${value.percent_limit}%, ${value.time_to_alert} ${this.getTimeUnitLabel(value.time_unit)}.`,
            value.lower_higher,
            value.measured_resource,
            this.getLowerHigherLanguageLabel(value.lower_higher),
            value.percent_limit,
            value.priority,
            value.rule_name,
            value.time_to_alert,
            value.time_unit,
            this.getRuleAlias(value.rule_alias, alias)
        );

    }

    private getTimeUnitLabel(timeUnit: TimeUnit): string {
        
        let labelTimeUnit = '';
        if (timeUnit === TimeUnit.Minutes) {
            labelTimeUnit = 'min';
        }

        return labelTimeUnit;

    }

    private getLowerHigherLanguageLabel(lowerHigher: LowerHigher): MessageContent {

        const selectedLang = this.translateService.currentLang;

        if (selectedLang === 'es') {
            if (lowerHigher === LowerHigher.Lower) {
                return MessageContent.LowerES;
            }

            return MessageContent.HigherES;
        } else if (selectedLang === 'en') {
            if (lowerHigher === LowerHigher.Lower) {
                return MessageContent.LowerEN;
            }

            return MessageContent.HigherEN;
        }

        if (lowerHigher === LowerHigher.Lower) {
            return MessageContent.LowerPT;
        }

        return MessageContent.HigherPT;

    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getRuleAlias(ruleAlias: any[], alias: Alias[]): RuleAlias[] {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return ruleAlias.map((ruleAlias: any) => new RuleAlias(alias.find(a => a.id === ruleAlias.alias) || new Alias(0, '', '', ''), ruleAlias.via));
    }

}